// ข่าว / ประชาสัมพันธ์ — the admin writes news and PR articles. Published articles appear on the website
// (home page, /newspaper, their own page) and in the daily edition from page 2, before customer notices.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ARTICLE_STATUS_LABEL, apiUrl,
  adminArticles, adminCreateArticle, adminUpdateArticle, adminDeleteArticle, adminImportWord,
} from "../../services/newspaperApi";
import { thaiDate } from "../../components/site/siteText";
import "./AdminEditor.css";
import { PageNav, PageSummary, usePaging } from "../../components/admin/Pager";
import "./AdminArticles.css";
import DateInput from "../../components/site/DateInput";

const todayIso = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
const EMPTY = { title: "", category: "", issueDate: "", sortOrder: "0", summary: "", body: "", author: "", imageCredit: "" };
const MAX_SOURCES = 10;
const FILTERS = [["", "ทั้งหมด"], ["draft", "ร่าง"], ["published", "เผยแพร่"]];

function ArticleForm({ article, categories, onSaved, onDeleted, onCancel }) {
  const [form, setForm] = useState(EMPTY);
  const [sources, setSources] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [removeIdx, setRemoveIdx] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileInput = useRef(null);
  const prevId = useRef(article?.id);
  const categoryKey = categories.join("|");

  useEffect(() => {
    // a brand-new article that was just saved keeps its confirmation message
    const justCreated = prevId.current === undefined && article?.id;
    prevId.current = article?.id;
    setForm(article
      ? {
          title: article.title || "",
          category: article.category || categories[0] || "",
          issueDate: article.issueDate || todayIso(),
          sortOrder: String(article.sortOrder ?? 0),
          summary: article.summary || "",
          body: article.body || "",
          author: article.author || "",
          imageCredit: article.imageCredit || "",
        }
      : { ...EMPTY, category: categories[0] || "", issueDate: todayIso() });
    setSources((article?.sources || []).map((x) => ({ name: x.name || "", url: x.url || "" })));
    setNewFiles([]);
    setRemoveIdx(new Set());
    if (!justCreated) setMsg(null);
    if (fileInput.current) fileInput.current.value = "";
    // reset only when a different article is opened (not when the same one is refreshed after saving)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id, categoryKey]);

  const previews = useMemo(
    () => newFiles.map((f) => ({ name: f.name, pdf: /\.pdf$/i.test(f.name), url: URL.createObjectURL(f) })),
    [newFiles]
  );
  const wordInput = useRef(null);

  // "ลงเป็น Word": the text of a .docx goes into the form (title when empty + body); the admin checks it, then saves
  const importWord = async (file) => {
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const { title, body } = await adminImportWord(file);
      setForm((f) => ({ ...f, title: f.title.trim() ? f.title : title, body }));
      setMsg({ ok: true, text: `ดึงข้อความจาก ${file.name} แล้ว · ตรวจทานแล้วกด "บันทึก"` });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
      if (wordInput.current) wordInput.current.value = "";
    }
  };
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (status, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("sources", JSON.stringify(sources.filter((x) => x.name.trim() || x.url.trim())));
      if (status) fd.append("status", status);
      if (article) fd.append("removeFiles", JSON.stringify([...removeIdx]));
      newFiles.forEach((f) => fd.append("files", f, f.name));
      const { article: saved } = article ? await adminUpdateArticle(article.id, fd) : await adminCreateArticle(fd);
      setNewFiles([]);
      setRemoveIdx(new Set());
      if (fileInput.current) fileInput.current.value = "";
      setMsg({ ok: true, text: okText });
      onSaved(saved);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`ลบข่าว "${article.title}" และรูปประกอบทั้งหมด? ลบแล้วกู้คืนไม่ได้`)) return;
    setBusy(true);
    try {
      await adminDeleteArticle(article.id);
      onDeleted(article.id);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
      setBusy(false);
    }
  };

  const setSource = (i, k) => (e) => setSources((list) => list.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));

  const toggleRemove = (i) => setRemoveIdx((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const published = article?.status === "published";
  const live = published && article.issueDate <= todayIso();

  return (
    <div className="adm-panel ed-detail site-form art-form">
      <div className="ed-detail-head">
        <div>
          <p className="eyebrow">{article ? "แก้ไขข่าว" : "เขียนข่าวใหม่"}</p>
          <h3>{form.title || "ข่าว / ประชาสัมพันธ์"}</h3>
        </div>
        <div className="ed-badges">
          {article && <span className={`adm-badge ${published ? "approved" : "submitted"}`}>{ARTICLE_STATUS_LABEL[article.status]}</span>}
          {article?.isTest && <span className="adm-badge test">ทดสอบ</span>}
        </div>
      </div>

      <div className="art-import">
        <p className="eyebrow">ลงจากไฟล์ Word</p>
        <p className="art-help">
          เลือกไฟล์ .docx ระบบจะดึงหัวข้อ เนื้อหา หัวข้อย่อย และตัวหนามาใส่ในช่องด้านล่างให้ · ต้องการลงเป็นรูปภาพหรือ PDF
          ให้แนบที่ "ไฟล์ประกอบ" ด้านล่าง
        </p>
        <input ref={wordInput} type="file" accept=".docx" disabled={busy} onChange={(e) => importWord(e.target.files?.[0])} />
      </div>

      <label>หัวข้อข่าว <span className="required">*</span><input value={form.title} onChange={set("title")} maxLength={200} /></label>
      <div className="form-row art-row-3">
        <label>
          ประเภท
          <select value={form.category} onChange={set("category")}>
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>วันลงฉบับ <span className="required">*</span><DateInput value={form.issueDate} onChange={set("issueDate")} /></label>
        <label>
          ลำดับในฉบับ
          <input type="number" min="0" max="999" value={form.sortOrder} onChange={set("sortOrder")} />
          <small className="art-help">เลขน้อยขึ้นหน้าก่อน (หน้า 2 คือข่าวลำดับแรก)</small>
        </label>
      </div>
      <label>
        ข้อความนำ / สรุปข่าว
        <textarea rows={2} value={form.summary} onChange={set("summary")} maxLength={600} />
      </label>
      <label>
        เนื้อหาข่าว
        <textarea rows={12} value={form.body} onChange={set("body")} />
        <small className="art-help">เว้น 1 บรรทัดว่างระหว่างย่อหน้า · หัวข้อย่อย: ขึ้นต้นบรรทัดด้วย # · ตัวหนา: **ข้อความ** · รายการ: ขึ้นต้นด้วย -</small>
      </label>

      <fieldset className="art-credit">
        <legend className="eyebrow">ที่มา / อ้างอิง / เครดิต</legend>
        <div className="form-row">
          <label>
            ผู้เขียน / เรียบเรียง
            <input value={form.author} onChange={set("author")} maxLength={120} placeholder="เช่น กองบรรณาธิการ มณีนพเก้า" />
          </label>
          <label>
            เครดิตภาพ
            <input value={form.imageCredit} onChange={set("imageCredit")} maxLength={200} placeholder="เช่น ภาพจาก ฝ่ายประชาสัมพันธ์" />
          </label>
        </div>
        <p className="art-help">แหล่งที่มา / อ้างอิง (สูงสุด {MAX_SOURCES} รายการ) · ลิงก์ไม่บังคับ ต้องขึ้นต้นด้วย http:// หรือ https://</p>
        {sources.map((x, i) => (
          <div key={i} className="art-source">
            <input value={x.name} onChange={setSource(i, "name")} maxLength={200} placeholder="ชื่อแหล่งที่มา เช่น ประกาศกรมพัฒนาธุรกิจการค้า" aria-label={`ชื่อแหล่งที่มา ${i + 1}`} />
            <input value={x.url} onChange={setSource(i, "url")} maxLength={500} placeholder="https://..." inputMode="url" aria-label={`ลิงก์แหล่งที่มา ${i + 1}`} />
            <button type="button" className="text-link ed-danger" onClick={() => setSources((list) => list.filter((_, j) => j !== i))}>ลบ</button>
          </div>
        ))}
        {sources.length < MAX_SOURCES && (
          <button type="button" className="text-link" onClick={() => setSources((list) => [...list, { name: "", url: "" }])}>+ เพิ่มแหล่งที่มา</button>
        )}
      </fieldset>

      <div className="ed-files">
        <p className="eyebrow">ไฟล์ประกอบ: รูป JPG / PNG หรือ PDF (รวมสูงสุด 6 ไฟล์)</p>
        <p className="art-help">PDF จะลงในหนังสือพิมพ์เต็มหน้าต่อจากหัวข่าว และมีปุ่มเปิดไฟล์บนหน้าเว็บ</p>
        {(article?.files || []).length === 0 && newFiles.length === 0 && <p className="adm-note">ยังไม่มีไฟล์ประกอบ</p>}
        <div className="art-thumbs">
          {(article?.files || []).map((f, i) => (
            <figure key={`old-${i}`} className={removeIdx.has(i) ? "removing" : ""}>
              {f.type === "application/pdf" ? (
                <a className="art-pdf-thumb" href={apiUrl(f.url)} target="_blank" rel="noreferrer">PDF<small>{f.name}</small></a>
              ) : (
                <img src={apiUrl(f.url)} alt={f.name} />
              )}
              <button type="button" className="text-link ed-danger" onClick={() => toggleRemove(i)}>
                {removeIdx.has(i) ? "ไม่ลบ" : "ลบไฟล์"}
              </button>
            </figure>
          ))}
          {previews.map((p) => (
            <figure key={p.url} className="new">
              {p.pdf ? <span className="art-pdf-thumb">PDF<small>{p.name}</small></span> : <img src={p.url} alt={p.name} />}
              <figcaption>ไฟล์ใหม่ (บันทึกแล้วจึงอัปโหลด)</figcaption>
            </figure>
          ))}
        </div>
        <input ref={fileInput} type="file" accept=".jpg,.jpeg,.png,.pdf" multiple onChange={(e) => {
          // at most 6 files in all (kept + new) — the server refuses more
          const room = Math.max(0, 6 - ((article?.files || []).length - removeIdx.size));
          const picked = Array.from(e.target.files || []);
          setNewFiles(picked.slice(0, room));
          if (picked.length > room) setMsg({ ok: false, text: `ไฟล์ประกอบข่าวได้สูงสุด 6 ไฟล์ · เพิ่มได้อีก ${room} ไฟล์` });
        }} />
      </div>

      {msg && <p className={`adm-msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</p>}

      <div className="ed-actions">
        {/* save = publish: admin news needs no review (owner, Edit1.pdf) */}
        <button
          type="button"
          className="button dark"
          disabled={busy}
          onClick={() => save(
            "published",
            form.issueDate > todayIso()
              ? `บันทึกแล้ว · จะขึ้นหน้าเว็บและหนังสือพิมพ์ฉบับวันที่ ${thaiDate(form.issueDate)}`
              : `บันทึกแล้ว · ขึ้นหน้าหนังสือพิมพ์ฉบับวันที่ ${thaiDate(form.issueDate)} แล้ว`
          )}
        >
          บันทึก <span>↗</span>
        </button>
        {published && (
          <button type="button" className="text-link" disabled={busy} onClick={() => save("draft", "ยกเลิกการเผยแพร่แล้ว · กลับเป็นร่าง")}>
            ยกเลิกเผยแพร่
          </button>
        )}
        {live && (
          <Link className="text-link" to={`/newspaper/articles/${article.id}`} target="_blank">
            ดูบนหน้าเว็บ <span>↗</span>
          </Link>
        )}
        {article && (
          <button type="button" className="text-link ed-danger" disabled={busy} onClick={remove}>ลบข่าว</button>
        )}
        <button type="button" className="text-link" disabled={busy} onClick={onCancel}>ปิด</button>
      </div>
    </div>
  );
}

function AdminArticles() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [data, setData] = useState({ articles: [], counts: {}, total: 0, categories: [] });
  const [selectedId, setSelectedId] = useState(null); // id, "new", or null
  // last saved copy: after "เผยแพร่" the article can leave the filtered list (e.g. the "ร่าง" tab) — keep the form open
  const [savedArticle, setSavedArticle] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError("");
    try {
      const result = await adminArticles(status, appliedQ);
      if (mine === seq.current) setData(result);
    } catch (e) {
      if (mine === seq.current) setError(e.message);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [status, appliedQ]);

  useEffect(() => { load(); }, [load]);

  const selected = selectedId === "new"
    ? null
    : data.articles.find((a) => a.id === selectedId) || (savedArticle?.id === selectedId ? savedArticle : null);
  const today = todayIso();
  const paging = usePaging(data.articles, "mng_articles_page_size", `${status}|${appliedQ}`);

  return (
    <div className="ed-root">
      <div className="art-top">
        <p className="adm-note">กด "บันทึก" แล้วข่าวขึ้นทันทีไม่ต้องสอบทาน: หน้าแรก หน้าหนังสือพิมพ์ หน้าข่าวของตัวเอง และฉบับรายวันของวันลงฉบับ (ตั้งแต่หน้า 2 ก่อนประกาศลูกค้า) · ลงได้หลายข่าวต่อวัน · พิมพ์เอง ดึงจาก Word หรือแนบรูป / PDF</p>
        <button type="button" className="button dark" onClick={() => setSelectedId("new")}>
          + เขียนข่าวใหม่ <span>✎</span>
        </button>
      </div>

      {selectedId && (
        <ArticleForm
          article={selected}
          categories={data.categories}
          onSaved={(saved) => { setSavedArticle(saved); setSelectedId(saved.id); load(); }}
          onDeleted={() => { setSelectedId(null); load(); }}
          onCancel={() => setSelectedId(null)}
        />
      )}

      <div className="ed-filters">
        {FILTERS.map(([value, label]) => (
          <button key={value || "all"} type="button" className={`ed-filter${status === value ? " active" : ""}`} onClick={() => setStatus(value)}>
            {label}
            <span>{value ? data.counts[value] || 0 : data.total}</span>
          </button>
        ))}
      </div>

      <form className="ed-search site-form" onSubmit={(e) => { e.preventDefault(); if (q.trim() === appliedQ) load(); else setAppliedQ(q.trim()); }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ค้นหาหัวข้อหรือเนื้อหาข่าว" />
        <button type="submit" className="button dark">ค้นหา</button>
      </form>

      {error && <p className="adm-msg err">{error}</p>}

      <div className="adm-panel ed-list">
        {loading && <p className="adm-note">กำลังโหลด...</p>}
        {!loading && data.articles.length === 0 && <p className="adm-note">ยังไม่มีข่าว กด &quot;+ เขียนข่าวใหม่&quot; เพื่อเริ่ม</p>}
        {!loading && data.articles.length > 0 && <PageSummary paging={paging} unit="ข่าว" />}
        {!loading && data.articles.length > 0 && (
          <table className="adm-table">
            <thead>
              <tr><th>วันลงฉบับ</th><th>หัวข้อ</th><th>ลำดับ</th><th>สถานะ</th></tr>
            </thead>
            <tbody>
              {paging.shown.map((a) => (
                <tr key={a.id} className={a.id === selectedId ? "selected" : ""} onClick={() => setSelectedId(a.id)}>
                  <td>{a.issueDate ? thaiDate(a.issueDate) : "-"}</td>
                  <td>
                    <strong>{a.title}</strong>
                    <small>{a.category}{(a.files || []).length ? ` · รูป ${a.files.length}` : ""}</small>
                  </td>
                  <td>{a.sortOrder}</td>
                  <td>
                    <span className={`adm-badge ${a.status === "published" ? "approved" : "submitted"}`}>{ARTICLE_STATUS_LABEL[a.status]}</span>
                    {a.isTest && <span className="adm-badge test">ทดสอบ</span>}
                    {a.status === "published" && a.issueDate > today && <small>รอถึงวันลงฉบับ</small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && <PageNav paging={paging} />}
      </div>
    </div>
  );
}

export default AdminArticles;
